import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Typography,
  Autocomplete,
  Box,
  CircularProgress,
  FormHelperText,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';
import closeIcon from '../../../../assets/accountSetting-assets/x-circle.svg';
import { classes, SalesAdminLabelTypo } from './utils';
import { ERROR } from '../../../../constants/staticContent';
import { addSalesAdminAndSalesCredit } from '../../../../services/paymentsApi';
import superManager from '../../../../services/superManager';
import ROLES from '../../../../constants/roles';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import { getLoggedInUserDetail } from '../../../../services/userApi';

const AddSalesAdminAndSalesCredit = ({ setOpen, setRefreshGrid }) => {
  const [option, setOption] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [credits, setCredits] = useState('');
  const [teamMember, setTeamMember] = useState({});
  const [creditError, setCreditError] = useState('');
  const [teamMemberError, setTeamMemberError] = useState('');
  const [currentUser, setCurrentUser] = useState({});
  const loginRole = getLocalStorageItem('role');
  const handleClose = () => {
    setOpen(false);
  };
  const getTeamMemberList = async () => {
    setIsLoading(true);
    const payload = {
      filterModel: {},
      dropDownValue: [],
    };
    const teamList = await superManager(payload);
    if (teamList) {
      const { data } = teamList;
      if (data && data?.rows && data.rows.length) {
        let result;
        if (
          loginRole === ROLES.SUPER_ADMIN ||
          loginRole === ROLES.SUPER_MANAGER
        ) {
          result = data?.rows.filter(
            (elem) =>
              ROLES.SALES_ADMIN === elem.role.role ||
              ROLES.SALES === elem.role.role,
          );
        } else if (loginRole === ROLES.SALES_ADMIN) {
          result = data?.rows.filter((elem) => ROLES.SALES === elem.role.role);
        }
        if (result && result.length > 0) {
          const teamMemberData = result.map((elem) => ({
            id: elem.id,
            value: `${elem.first_name} ${elem.last_name}`,
            label: `${elem.first_name} ${elem.last_name}`,
          }));
          setOption(teamMemberData);
        } else {
          setOption([]);
        }
      } else {
        setOption([]);
      }
    }

    setIsLoading(false);
  };
  useEffect(() => {
    const fetchLoggedInUser = async () => {
      try {
        const loggedInUser = await getLoggedInUserDetail();
        setCurrentUser(loggedInUser);
      } catch (error) {
        console.error('Error fetching logged-in user details:', error);
      }
    };

    fetchLoggedInUser();
  }, []);

  useEffect(() => {
    getTeamMemberList();
  }, []);
  const getTeamMemberError = (organization) => {
    if (organization === '') {
      return ERROR.EMPTY_ERROR;
    }
    return '';
  };
  const handleChange = (data) => {
    setTeamMemberError(getTeamMemberError(data.value));
    setTeamMember({ id: data.id, name: data.value });
  };

  const getCreditError = (credit) => {
    if (credit === '') {
      return ERROR.EMPTY_ERROR;
    }

    if (Number(credit) <= 0) {
      return ERROR.VALUE_MUST_BE_GREATER_THAN_ZERO; // Handles 0 input
    }
    if (loginRole === ROLES.SALES_ADMIN) {
      if (
        currentUser?.creditHistories === null ||
        currentUser?.creditHistories?.remaining_credits === 0
      ) {
        return ERROR.ZERO_CREDIT_ERROR;
      }
      if (
        credit &&
        Number(credit) > currentUser?.creditHistories?.remaining_credits
      ) {
        return ERROR.CREDIT_ERROR;
      }
    }

    return '';
  };
  const handleChangeCredit = (data) => {
    setCreditError(getCreditError(data));
    setCredits(data);
  };
  const handleAddCredits = async () => {
    const creditValidation = getCreditError(credits);
    const teamMemberValue =
      Object.keys(teamMember).length > 0 ? teamMember.organization : '';
    const teamMemberValidation = getTeamMemberError(teamMemberValue);
    if (creditValidation) {
      setCreditError(creditValidation);
    }
    if (teamMemberValidation) {
      setTeamMemberError(teamMemberValidation);
    }
    if (!creditError && !teamMemberValidation && !creditValidation) {
      setIsLoading(true);
      const response = await addSalesAdminAndSalesCredit({
        credit: credits,
        salesId: teamMember.id,
      });
      setIsLoading(false);
      if (response) {
        handleClose();
        setRefreshGrid((prev) => !prev);
      }
    }
  };
  return (
    <Card className={classes.cardComponent}>
      <CardHeader
        className={classes.cardHeader}
        title={
          <Grid container alignItems="center">
            <Grid item xs={10}>
              <Typography variant="h6">Add Credits</Typography>
            </Grid>
            <Grid item xs={2} align="right">
              <Avatar
                onClick={handleClose}
                src={closeIcon}
                className={classes.closeAction}
              />
            </Grid>
          </Grid>
        }
      />
      {isLoading ? (
        <Box className={classes.loaderBox}>
          <CircularProgress />
        </Box>
      ) : (
        <CardContent className={classes.cardContent}>
          {loginRole === ROLES.SALES_ADMIN && (
            <Box>
              <SalesAdminLabelTypo variant="subtitle1">
                Remaining Credits
              </SalesAdminLabelTypo>

              <TextField
                fullWidth
                size="small"
                id="outlined-basic"
                variant="outlined"
                className={classes.textField}
                type="number"
                disabled
                value={
                  currentUser
                    ? currentUser?.creditHistories?.remaining_credits
                    : 0
                }
              />
            </Box>
          )}
          <Box>
            <SalesAdminLabelTypo variant="subtitle1">
              Team Member
            </SalesAdminLabelTypo>
            <Autocomplete
              options={option}
              size="small"
              onChange={(event, data) => {
                if (data) {
                  handleChange(data);
                }
              }}
              ListboxProps={{ className: 'Autocomplete_scrollbar' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Team Member"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
            {teamMemberError && (
              <FormHelperText className={classes.formHelperText}>
                {teamMemberError}
              </FormHelperText>
            )}
          </Box>
          <Box>
            <SalesAdminLabelTypo variant="subtitle1">
              Credits
            </SalesAdminLabelTypo>

            <TextField
              fullWidth
              size="small"
              id="outlined-basic"
              variant="outlined"
              className={classes.textField}
              type="number"
              error={Boolean(creditError)}
              helperText={creditError}
              onChange={(event) => {
                const { value } = event.target;
                handleChangeCredit(value);
              }}
            />
          </Box>
          <LoadingButton
            variant="contained"
            alignItems="center"
            className={classes.addUserBtn}
            onClick={handleAddCredits}
          >
            Add
          </LoadingButton>
        </CardContent>
      )}
    </Card>
  );
};

AddSalesAdminAndSalesCredit.propTypes = {
  setOpen: PropTypes.func.isRequired,
  setRefreshGrid: PropTypes.func.isRequired,
};

export default AddSalesAdminAndSalesCredit;
